import { fractionToAmountCents } from '@utils/allocationUnits';
import { capacityViolationKeys } from './allocationCapacity';
import { allocationCellKey } from './allocationCellKey';
import {
  ALLOCATION_WEIGHT_TOLERANCE,
  dollarToleranceCents,
} from './allocationTolerances';
import { buildHouseholdRowTargetCents } from './lockedMatrixRedistribute';

export const AllocationViolationReasons = {
  ROW_DRIFT: 'rowDrift',
  WEIGHT_SUM: 'weightSum',
  CAPACITY: 'capacity',
} as const;

export type AllocationViolationReason =
  (typeof AllocationViolationReasons)[keyof typeof AllocationViolationReasons];

type HighlightCell = { sa: string; fp: string; fraction: number };
type HighlightMember = { sa: string; mvCents: number };

const mvCentsBySubAccountId = (members: HighlightMember[]) =>
  new Map(members.map((member) => [member.sa, member.mvCents]));

const weightSumViolationCells = (
  members: HighlightMember[],
  cells: HighlightCell[],
  securityMassFraction: number,
) => {
  const violationCells = new Set<string>();
  for (const member of members) {
    // Weights derive from dollars, so a zero-MV member always reads 0 and can
    // never sum to the investable share. Its dollar total is 0 either way.
    if (member.mvCents <= 0) continue;

    const memberCells = cells.filter((c) => c.sa === member.sa);
    const weightSum = memberCells.reduce((s, c) => s + c.fraction, 0);
    if (
      Math.abs(weightSum - securityMassFraction) > ALLOCATION_WEIGHT_TOLERANCE
    ) {
      for (const cell of memberCells) {
        violationCells.add(allocationCellKey(cell.sa, cell.fp));
      }
    }
  }
  return violationCells;
};

export const evaluateAllocationViolations = ({
  members,
  rowTargetFraction,
  cells,
  securityMassFraction = 1,
  capacityFractionByKey,
}: {
  members: HighlightMember[];
  rowTargetFraction: Map<string, number>;
  cells: HighlightCell[];
  securityMassFraction?: number;
  capacityFractionByKey?: ReadonlyMap<string, number>;
}): {
  violationCells: Set<string>;
  reasons: Set<AllocationViolationReason>;
} => {
  const violationCells = new Set<string>();
  const reasons = new Set<AllocationViolationReason>();

  const weightSumCells = weightSumViolationCells(
    members,
    cells,
    securityMassFraction,
  );
  if (weightSumCells.size > 0)
    reasons.add(AllocationViolationReasons.WEIGHT_SUM);
  for (const key of weightSumCells) violationCells.add(key);

  const mvTotalCents = members.reduce((s, m) => s + m.mvCents, 0);
  const mvCentsBySa = mvCentsBySubAccountId(members);
  const targetRowCentsMap = buildHouseholdRowTargetCents(
    rowTargetFraction,
    mvTotalCents,
    securityMassFraction,
  );

  for (const [fp] of rowTargetFraction) {
    const rowCells = cells.filter((c) => c.fp === fp);
    const expectedRowCents = targetRowCentsMap.get(fp) ?? 0;
    const actualRowCents = rowCells.reduce(
      (s, c) =>
        s + fractionToAmountCents(c.fraction, mvCentsBySa.get(c.sa) ?? 0),
      0,
    );
    const rowDrift = Math.abs(actualRowCents - expectedRowCents);
    if (rowDrift > dollarToleranceCents(members.length)) {
      reasons.add(AllocationViolationReasons.ROW_DRIFT);
      for (const cell of rowCells) {
        violationCells.add(allocationCellKey(cell.sa, cell.fp));
      }
    }
  }

  const capacityKeys = capacityViolationKeys({
    cells,
    members,
    capacityFractionByKey,
  });
  if (capacityKeys.size > 0) reasons.add(AllocationViolationReasons.CAPACITY);
  for (const key of capacityKeys) violationCells.add(key);

  return { violationCells, reasons };
};
